const STORAGE_KEY = "moltjobs_post_auth_intent_v1";
const INTENT_TTL_MS = 2 * 60 * 60 * 1000;

type StoredPostAuthIntent = {
  path: string;
  savedAt: number;
};

function sanitizeInternalPath(path: string): string | null {
  if (
    typeof window === "undefined" ||
    !path.startsWith("/") ||
    path.startsWith("//")
  ) {
    return null;
  }

  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname === "/login" || url.pathname === "/auth/callback") {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function rememberPostAuthPath(path: string) {
  if (typeof window === "undefined") return;
  const safePath = sanitizeInternalPath(path);
  if (!safePath) return;

  try {
    const intent: StoredPostAuthIntent = {
      path: safePath,
      savedAt: Date.now(),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Storage can be unavailable in hardened browsers. The normal dashboard
    // fallback still works, so preserving the intent is best-effort.
  }
}

export function consumePostAuthPath(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;

    const intent = JSON.parse(raw) as Partial<StoredPostAuthIntent>;
    if (
      typeof intent.path !== "string" ||
      typeof intent.savedAt !== "number" ||
      Date.now() - intent.savedAt > INTENT_TTL_MS
    ) {
      return null;
    }

    return sanitizeInternalPath(intent.path);
  } catch {
    return null;
  }
}
